import { Link } from "react-router-dom";
import { ArrowLeft, FileText, Mail, MapPin, ShieldCheck } from "lucide-react";

const LEGAL = [
  { to: "/legal/terms-of-service", label: "Terms of Service", tid: "legal-link-terms" },
  { to: "/legal/privacy-policy", label: "Privacy Policy", tid: "legal-link-privacy" },
  { to: "/legal/refund-and-cancellation-policy", label: "Refund & Cancellation Policy", tid: "legal-link-refund" },
  { to: "/legal/acceptable-use-and-content-policy", label: "Acceptable Use & Content Policy", tid: "legal-link-acceptable-use" },
  { to: "/legal/child-safety", label: "Child Safety Standards", tid: "legal-link-child-safety" },
];

// Standalone "Legal & Support" page — holds everything that used to live in the in-app footer.
export default function LegalHubPage() {
  const year = new Date().getFullYear();
  return (
    <div className="min-h-screen bg-black text-white" data-testid="legal-hub-page">
      <header className="border-b border-white/5">
        <div className="max-w-4xl mx-auto px-6 py-6 flex items-center justify-between">
          <Link to="/feed" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-zinc-400 hover:text-rose-400" data-testid="legal-hub-back">
            <ArrowLeft size={14} /> Back to My Panda X
          </Link>
          <span className="text-[10px] uppercase tracking-[0.3em] text-zinc-600">Adults 18+ only</span>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-14 space-y-14">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-rose-500 mb-3">My Panda X</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-white">Legal &amp; Support</h1>
          <p className="mt-4 text-sm text-zinc-400 max-w-xl">A premium community for adult creators. Adults 18+ only. Our policies and how to reach us, all in one place.</p>
        </div>

        <section data-testid="legal-hub-policies">
          <h2 className="text-base md:text-lg text-white flex items-center gap-2 mb-5"><FileText size={16} className="text-rose-500" /> Legal</h2>
          <ul className="grid gap-3 sm:grid-cols-2">
            {LEGAL.map((l) => (
              <li key={l.to}>
                <Link to={l.to} data-testid={l.tid} className="block border border-white/10 bg-zinc-950/60 px-5 py-4 text-sm text-zinc-200 hover:border-rose-500/60 hover:text-white transition-colors">
                  {l.label} <span className="text-zinc-600">→</span>
                </Link>
              </li>
            ))}
          </ul>
        </section>

        <section data-testid="legal-hub-support">
          <h2 className="text-base md:text-lg text-white flex items-center gap-2 mb-5"><ShieldCheck size={16} className="text-rose-500" /> Customer Support</h2>
          <ul className="space-y-4 text-sm text-zinc-300">
            <li className="flex items-start gap-3">
              <Mail className="w-4 h-4 mt-0.5 shrink-0 text-rose-500" aria-hidden="true" />
              <a href="mailto:support@mypandax.com" className="hover:text-rose-400 transition-colors" data-testid="legal-hub-support-email">support@mypandax.com</a>
            </li>
            <li className="flex items-start gap-3">
              <MapPin className="w-4 h-4 mt-0.5 shrink-0 text-rose-500" aria-hidden="true" />
              <span data-testid="legal-hub-support-address">108 Walltown St, Lexington, NC 27292</span>
            </li>
          </ul>
          <p className="mt-6 text-xs text-zinc-500 leading-relaxed" data-testid="legal-hub-cancellation">
            Cancel any time in <Link to="/creator" className="underline hover:text-rose-400">account settings</Link> or by emailing support.
            See the <Link to="/support" className="underline hover:text-rose-400">Support Center</Link> for account, billing, safety and technical help.
          </p>
        </section>

        <div className="pt-8 border-t border-white/5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-[10px] uppercase tracking-[0.3em] text-zinc-600">
          <div data-testid="legal-hub-copyright">© {year} My Panda X · mypandax.com</div>
          <div>Adults 18+ only</div>
        </div>
      </main>
    </div>
  );
}
